import { useCallback } from 'react';
import { useUserApiFacade } from '../../../../apis/facades/useUserApiFacade';
import type { UserPreferencesTableColumnSettings } from '@server/user-preferences';
import { telemetry } from '../../../../telemetry';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectUserTableColumnSettings,
  setUserTableColumnsSettings as setUserTableColumnsSettingsAction,
} from '../../../../reducers/userInfoReducer';
import type { DataTableBaseColumn } from '../TableTypes';
import type { DataTableId } from '../utils';

export const useManipulateColumns = (tableId: DataTableId) => {
  const dispatch = useDispatch();
  const { patch_userPreferences } = useUserApiFacade();
  const tableColumnSettings = useSelector(selectUserTableColumnSettings);

  const setUserTableColumnsSettings = useCallback(
    async (tableColumnSettings: UserPreferencesTableColumnSettings[]) => {
      try {
        dispatch(setUserTableColumnsSettingsAction(tableColumnSettings));
        await patch_userPreferences({ tableColumnSettings });
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [patch_userPreferences, dispatch],
  );

  const onColumnOrderChange = useCallback(
    (columns: DataTableBaseColumn[]) => {
      const currentTableSettings = tableColumnSettings?.find(
        (setting) => setting.tableId === tableId,
      );

      const columnsOrder: { [key: string]: number } = {};
      columns.forEach((col, idx) => {
        columnsOrder[col.field] = idx;
      });

      if (!currentTableSettings) {
        setUserTableColumnsSettings([
          ...(tableColumnSettings ?? []),
          { tableId, order: columnsOrder },
        ]);
      } else {
        const newTableColumnSettings = (tableColumnSettings ?? []).map((setting) => {
          if (setting.tableId === tableId) {
            return {
              ...setting,
              order: columnsOrder,
            };
          }
          return setting;
        });
        setUserTableColumnsSettings(newTableColumnSettings);
      }
    },
    [tableColumnSettings, setUserTableColumnsSettings, tableId],
  );

  const onColumnResize = useCallback(
    (columnsWidth: Record<string, number>) => {
      const currentTableSettings = tableColumnSettings?.find(
        (setting) => setting.tableId === tableId,
      );

      if (!currentTableSettings) {
        setUserTableColumnsSettings([...(tableColumnSettings ?? []), { tableId, columnsWidth }]);
      } else {
        const newTableColumnSettings = (tableColumnSettings ?? []).map((setting) => {
          if (setting.tableId === tableId) {
            return {
              ...setting,
              columnsWidth,
            };
          }
          return setting;
        });
        setUserTableColumnsSettings(newTableColumnSettings);
      }
    },
    [tableColumnSettings, setUserTableColumnsSettings, tableId],
  );

  return { setUserTableColumnsSettings, onColumnOrderChange, onColumnResize };
};
